import {extendObservable, reaction, action} from 'mobx';
import {Exclude, Expose, classToPlain} from "class-transformer";
import {simplyDeserialize} from "../../common_components/serialization/common";
import {safeMomentDate} from "./functions";
import moment from 'moment';

@Exclude()
export default class FeedbackComment{

    constructor(store, id, feedbackChain, persisted = true) {
        this.store = store;
        extendObservable(this, {
            @Expose()
            id: id,

            @Expose()
            internal: false,

            @Expose()
            description: '',

            createdAt: undefined,

            updatedAt: undefined,

            @Expose()
            attachments: [],

            persisted: persisted,

            feedbackChain: feedbackChain,

            creator: {},

            attachmentFilesQueue: [],

            isEditing: false,
        });

        this.autoUpdate = true;

        // Automatically update in backend when there is any change in
        // the tracked data
        this.updateDisposer = reaction( () => {
                let trackedData = {
                    description: this.description,
                    internal: this.internal,
                    // persisted: this.persisted,

                    // Sensitive to isEditing to automatically save
                    // the comment when the isEditing passes from true to false.
                    isEdition: this.isEditing
                };
                return trackedData;
            },
            () => {
                if( this.persisted && this.autoUpdate && !this.isEditing ){
                    this.updatedAt = moment();
                    if( this.store ){
                        this.store.updateBackend(this);
                    }
                }
            });

        this.addToStore();
    }

    addToStore(){
        if( this.store && this.feedbackChain && this.feedbackChain.id && this.persisted ){
            this.store.addFeedbackComment(this);
        }
    }

    serialize(){
        let basicSerialize = classToPlain(this);
        basicSerialize["createdAt"] = this.createdAt;
        basicSerialize["updatedAt"] = this.updatedAt;
        basicSerialize["feedbackChainId"] = this.feedbackChain.id;
        return basicSerialize;
    }

    toJson(){
        return this.serialize();
    }

    static deserialize(json, feedbackChain, store){
        let result = simplyDeserialize(FeedbackComment, json);
        result["createdAt"] = safeMomentDate(json.createdAt);
        result["updatedAt"] = safeMomentDate(json.updatedAt);
        result.feedbackChain = feedbackChain;
        result.store = store;
        result.id = json.id;
        result.creator = json.creator;
        result.attachments = store.attachmentsStore.attachmentsFromJsonArray(json.attachments, result);
        return result;
    }

    persist(){
        this.createdAt = this.updatedAt = moment();
        if( this.feedbackChain ){
            this.feedbackChain.updatedAt = moment();
        }
        this.store.createBackend(this);
        this.feedbackChain.comments.push(this);
    }

    destroy(){
        this.store.destroy(this);
        this.dispose();
    }

    dispose(){
        this.updateDisposer();
        this.feedbackChain.comments.remove(this);
    }

    addAttachment(file){
        this.attachmentFilesQueue.push(file);
        // Save attachments only if the attachable has already been persisted in backend.
        if( this.persisted ) {
            this.persistAttachments();
        }
    }

    removeFileFromQueue(file){
        this.attachmentFilesQueue.remove(file);
    }

    persistAttachments(){
        if( this.persisted ){
            while( this.attachmentFilesQueue.length > 0 ){
                const file = this.attachmentFilesQueue.pop();
                this.store.attachmentsStore.create(file, this);
            }
        }
        else{
            throw "AttachmentOnInvalidObjectException";
        }
    }

    canEditAttachments(){
        return this.store.rootStore.authorizationsStore.canEditComment(this);
    }

    // val is boolean
    @action
    setIsEditing(val){
        if( val == true ){
            this.originalDescription = this.description;   
        }
        this.isEditing = val;
    }

    // val is boolean
    @action
    setInternal(val){
        this.internal = val;
    }

    @action
    cancelEditing(){
        this.description = this.originalDescription;
        this.setIsEditing(false);
    }

}

// Attributes to be included in deserialization and copies
// See common_components/serialization/common.js
FeedbackComment.allowedInstanceProperties = ['description', 'internal'];

// Required by the Attachments store
FeedbackComment.backendModelName = 'FeedbackComment';